import { useQuery } from '@apollo/client';
import { css } from '@emotion/css';
import { Headline2, HighlightBar } from '@volumental/common';
import { useState, useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from '../../../api/gql';
import { useInputStore, useSessionStore } from '../../../store';
import {
  type SizeRecommendation,
  type RecommendationError,
} from '../../../types/types';
import { normalizeUrl } from '../../../util/get-url';
import { handleRecommendationResult } from '../../../util/utils';
import { ScannerBorderedButton } from './scanner-bordered-button';

const containerStyle = css`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const alignLeftStyle = css`
  width: 100%;
  display: flex;
  justify-self: left;
`;
const GetScannerControllerQuery = graphql(`
  query getScannerControllerQuery(
    $productID: String!
    $brandName: String
    $genderAge: String
    $scanID: ID
    $previousPurchaseID: ID
    $sizeLocale: SizeLocale
    $sessionID: String
    $url: String!
    $includeRecommendation: Boolean!
  ) {
    configuration {
      features {
        recommendation {
          displayFootWidth
        }
      }
    }
    ...RecommendationQueryFragment @include(if: $includeRecommendation)
  }
`);

export function ScannerController(): JSX.Element {
  const { t } = useTranslation();
  const {
    productID,
    brandName,
    genderAge,
    sizeLocale,
    deviceId: sessionID,
    callbackUrl,
    widgetComponentType,
  } = useSessionStore((store) => store);
  const { scanID, previousPurchaseID } = useInputStore((store) => store);
  const { loading, error, data, variables } = useQuery(
    GetScannerControllerQuery,
    {
      variables: {
        productID: productID ?? '',
        brandName,
        genderAge,
        scanID,
        sizeLocale,
        sessionID,
        previousPurchaseID,
        url: normalizeUrl(callbackUrl),
        includeRecommendation: Boolean(scanID ?? previousPurchaseID),
      },
      errorPolicy: 'all',
    },
  );

  const [result, setResult] = useState<
    | { recommendation?: SizeRecommendation; error?: RecommendationError }
    | undefined
  >();

  useEffect(() => {
    if (variables?.includeRecommendation) {
      const res = handleRecommendationResult(
        variables,
        data,
        error,
        widgetComponentType,
      );
      setResult(res);
    }
  }, [data, error, variables, widgetComponentType]);

  const showWidth =
    data?.configuration.features.recommendation.displayFootWidth;

  return (
    <div className={containerStyle}>
      <Headline2
        style={{ width: '100%', lineHeight: 'normal', marginBottom: '1rem' }}
      >
        <HighlightBar>{t('sizeChartScannerTitle')}</HighlightBar>
      </Headline2>
      <div className={alignLeftStyle}>
        <ScannerBorderedButton
          recommendedSize={result?.recommendation}
          error={result?.error}
          showWidth={showWidth}
          loading={loading}
        />
      </div>
    </div>
  );
}
